import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookMarked, Camera, FolderOpen, Sparkles, Fingerprint, ArrowRight, Brain, CalendarClock, Users } from "lucide-react";
import api from "../lib/api";
import PwaInstallPrompt from "../components/PwaInstallPrompt";

const DEFAULT_POSTER = "https://customer-assets.emergentagent.com/job_student-glossary/artifacts/l18j39r9_main%20page.png";

export default function Landing() {
  const [video, setVideo] = useState(null);
  const [poster, setPoster] = useState(DEFAULT_POSTER);
  const [trending, setTrending] = useState(null);
  useEffect(() => {
    api.get("/landing/video").then((r) => {
      if (r.data.status === "ready" && r.data.video_url) setVideo(r.data.video_url);
    }).catch(() => {});
    api.get("/landing/poster").then((r) => {
      if (r.data?.url) setPoster(r.data.url);
    }).catch(() => {});
    api.get("/social/trending").then((r) => setTrending(r.data)).catch(() => {});
  }, []);

  const features = [
    { icon: BookMarked, title: "Smart Glossary", desc: "Build your personal dictionary, organised by subject." },
    { icon: Camera, title: "Snap-to-Vocab", desc: "Photograph any textbook page; AI extracts key terms." },
    { icon: Brain, title: "AI Quiz", desc: "Test yourself, then crush your weak spots." },
    { icon: CalendarClock, title: "Test Reminders", desc: "Push notifications when an exam is around the corner." },
    { icon: FolderOpen, title: "Study Vault", desc: "Store notes, PDFs, recordings — all in one place." },
    { icon: Fingerprint, title: "Biometric Login", desc: "Face ID or fingerprint via passkeys." },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="px-6 sm:px-8 lg:px-12 py-6 flex items-center justify-between border-b-2 border-slate-900 bg-white">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-yellow-300 border-2 border-slate-900 rounded-md grid place-items-center" style={{ boxShadow: "3px 3px 0 0 #0F172A" }}>
            <BookMarked className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <span className="font-display text-2xl font-black">Studict</span>
        </div>
        <Link to="/login" data-testid="header-login-link" className="nb-btn-primary inline-flex items-center gap-2">
          Get started <ArrowRight className="w-4 h-4" strokeWidth={2.5}/>
        </Link>
      </header>

      <section className="px-6 sm:px-8 lg:px-12 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
        <div>
          <span className="nb-tag" style={{ background: "#86EFAC" }} data-testid="hero-tag">Built for students, by students</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mt-6 leading-[1.05]">
            Your personal dictionary.<br/>
            <span className="bg-yellow-300 px-2 inline-block border-2 border-slate-900 rounded-md" style={{ boxShadow: "4px 4px 0 0 #0F172A"}}>Built for class.</span>
          </h1>
          <p className="text-lg font-medium text-slate-700 mt-6 leading-relaxed max-w-xl">
            Snap a textbook page, watch the AI pull out the key vocabulary, organise everything by subject, quiz yourself, and keep your study materials in one tidy vault.
          </p>
          <div className="flex flex-wrap gap-4 mt-8">
            <Link to="/login" data-testid="hero-cta" className="nb-btn-primary inline-flex items-center gap-2 text-lg">
              <Sparkles className="w-5 h-5" strokeWidth={2.5}/> Start free
            </Link>
            <Link to="/pricing" data-testid="hero-pricing" className="nb-btn-secondary inline-flex items-center gap-2 text-lg">
              See plans
            </Link>
          </div>
          <p className="text-sm font-medium text-slate-500 mt-4">Free 100&nbsp;MB · Pro from £8.99/mo · iOS, Android &amp; web</p>
        </div>

        <div className="nb-card p-3" data-testid="hero-media">
          {video ? (
            <video
              key={video}
              src={video}
              autoPlay
              loop
              muted
              playsInline
              controls
              poster={poster}
              className="w-full rounded-md border-2 border-slate-900 aspect-video object-cover bg-black"
            />
          ) : (
            <img src={poster} alt="Studict — built for students, by students" className="w-full rounded-md border-2 border-slate-900"/>
          )}
        </div>
      </section>

      <section className="px-6 sm:px-8 lg:px-12 py-12 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="nb-tag" style={{ background: "#FDBA74" }}>Your words, your way</span>
            <h2 className="font-display text-3xl sm:text-4xl font-black mt-4 leading-tight">
              Add definitions you like.<br/>
              <span className="bg-yellow-300 px-2 inline-block border-2 border-slate-900 rounded-md mt-2" style={{ boxShadow: "4px 4px 0 0 #0F172A" }}>Your categories. Your colours.</span>
            </h2>
            <p className="text-base font-medium text-slate-700 mt-5 leading-relaxed max-w-lg">
              Type a word. Tap <strong>AI suggest</strong> for a starter definition, or write it in your own words — exactly the way your teacher explained it. Add your own example, your notes, and tag it with whatever subject you're studying.
            </p>
            <ul className="mt-5 space-y-2.5 text-sm font-medium text-slate-800">
              <li className="flex items-start gap-2"><span className="nb-tag" style={{ background: "#86EFAC" }}>1</span> <span>Create custom categories — Biology, History, Spanish, anything — pick a colour each.</span></li>
              <li className="flex items-start gap-2"><span className="nb-tag" style={{ background: "#7DD3FC" }}>2</span> <span>Write definitions in your own words; AI is there if you get stuck.</span></li>
              <li className="flex items-start gap-2"><span className="nb-tag" style={{ background: "#D8B4FE" }}>3</span> <span>Search and filter your glossary by subject in a tap.</span></li>
            </ul>
          </div>

          <div className="relative">
            {/* Categories row */}
            <div className="flex flex-wrap gap-3 mb-5" data-testid="landing-cat-demo">
              {[
                { n: "Biology", c: "#86EFAC" },
                { n: "History", c: "#FDBA74" },
                { n: "Mathematics", c: "#7DD3FC" },
                { n: "Literature", c: "#D8B4FE" },
                { n: "+ New", c: "#FFFFFF" },
              ].map((cat) => (
                <span key={cat.n} className="nb-tag px-4 py-1.5 text-sm" style={{ background: cat.c }}>{cat.n}</span>
              ))}
            </div>

            {/* Sample word card */}
            <article className="nb-card p-5 bg-white" data-testid="landing-word-demo">
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-display text-2xl font-bold">Photosynthesis</h3>
                <span className="nb-tag" style={{ background: "#86EFAC" }}>Biology</span>
              </div>
              <p className="text-sm text-slate-800 mt-3 leading-relaxed">
                The process plants use to turn sunlight, water, and carbon dioxide into food (glucose) and oxygen. <em className="text-slate-500">— in my own words ✨</em>
              </p>
              <p className="text-xs text-slate-500 mt-3 italic">"During photosynthesis, leaves act like tiny solar panels."</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="nb-tag text-xs" style={{ background: "#FDE047" }}>Mrs Khan said this on Tuesday</span>
                <span className="nb-tag text-xs" style={{ background: "#FFFFFF" }}>Test Friday</span>
              </div>
            </article>

            {trending && (
              <p className="mt-3 text-xs font-bold text-slate-700 inline-flex items-center gap-1.5" data-testid="trending-counter">
                <Users className="w-3.5 h-3.5" strokeWidth={2.5}/>
                {trending.count.toLocaleString()} students added &ldquo;{trending.term}&rdquo; in the last {trending.days} days.
              </p>
            )}

            <div className="absolute -top-4 -right-4 hidden sm:block rotate-6 nb-card-sm p-2.5 bg-yellow-300 text-xs font-bold">
              <Sparkles className="w-4 h-4 inline -mt-1" strokeWidth={2.5}/> AI suggested this
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 sm:px-8 lg:px-12 py-12 max-w-7xl mx-auto">
        <h2 className="font-display text-3xl sm:text-4xl font-black mb-8">Everything you need to study smart</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
          {features.map(({ icon: Icon, title, desc }, i) => {
            const colors = ["#FDE047", "#7DD3FC", "#86EFAC", "#FDBA74", "#D8B4FE", "#F9A8D4"];
            return (
              <div key={title} className="nb-card p-6" style={{ background: colors[i] }}>
                <Icon className="w-8 h-8" strokeWidth={2.5}/>
                <h3 className="font-display text-xl font-bold mt-3">{title}</h3>
                <p className="text-sm font-medium mt-2 text-slate-800">{desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      <footer className="px-6 sm:px-8 lg:px-12 py-8 mt-12 border-t-2 border-slate-900 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm font-medium text-slate-600">© Studict — A study companion that doesn't pretend to be a teacher.</p>
          <div className="flex flex-wrap items-center gap-4 text-sm font-bold">
            <Link to="/privacy" data-testid="footer-privacy-link" className="underline hover:no-underline">Privacy</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="underline hover:no-underline">Terms</Link>
            <Link to="/support" data-testid="footer-support-link" className="underline hover:no-underline">Contact support</Link>
          </div>
        </div>
      </footer>
      <PwaInstallPrompt />
    </div>
  );
}
